// frontend/src/layouts/user-table/components/EditUserDialog.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import api from "api/axios";
import { fetchUser } from "services/users";
import { ensureCsrf } from "services/auth";

/** Convertit un rôle éventuel (string ou objet) en string exploitable */
function toRoleString(role) {
  if (!role) return "";
  if (typeof role === "string") return role;
  // objets possibles: { name }, { slug }, { value }, { id }, { label }
  return (
    role.value ??
    role.slug ??
    role.name ??
    (typeof role.id !== "undefined" ? String(role.id) : "") ??
    ""
  );
}

/** Normalise un payload utilisateur en structure stable pour le formulaire */
function normalizeUser(u = {}) {
  return {
    username: u.username ?? "",
    email: u.email ?? "",
    first_name: Object.prototype.hasOwnProperty.call(u, "first_name") ? (u.first_name ?? "") : "",
    last_name: Object.prototype.hasOwnProperty.call(u, "last_name") ? (u.last_name ?? "") : "",
    is_active: Object.prototype.hasOwnProperty.call(u, "is_active") ? !!u.is_active : true,
    // accepte u.global_role (objet ou string) ou u.role (fallback)
    global_role: toRoleString(u.global_role ?? u.role ?? ""),
  };
}

/** Transforme availableRoles (strings ou objets) en {value,label} */
function normalizeRoleOptions(availableRoles) {
  if (!Array.isArray(availableRoles)) return [];
  return availableRoles.map((r) =>
    typeof r === "string"
      ? { value: r, label: r }
      : {
          value: toRoleString(r),
          label: r.label ?? r.name ?? toRoleString(r) ?? "",
        }
  );
}

export default function EditUserDialog({ open, userId, onClose, onSaved, availableRoles }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(normalizeUser());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Charge les infos utilisateur quand la modale s’ouvre
  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!open || !userId) return;
      setLoading(true);
      setError("");

      try {
        const u = await fetchUser(userId); // { id, username, email, ... }
        if (!mounted) return;

        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[EditUserDialog] fetchUser payload:", u);
        }

        setForm(normalizeUser(u));
      } catch (e) {
        setError("Impossible de charger l’utilisateur.");
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("[EditUserDialog] fetchUser error:", e);
        }
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [open, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setForm((f) => ({ ...f, [name]: checked }));
  };

  const handleSave = async () => {
    if (!userId) {
      setError("Identifiant utilisateur manquant.");
      return;
    }

    setSaving(true);
    setError("");

    // ✅ s’assure que le cookie CSRF est posé (utile si tu viens d’arriver sur la page)
    await ensureCsrf();

    // "" = retirer le rôle ; undefined = ne pas toucher ; string = affecter
    const roleField =
      form.global_role === ""
        ? ""
        : typeof form.global_role === "string"
          ? form.global_role
          : undefined;

    const payload = {
      email: form.email,
      first_name: form.first_name,
      last_name: form.last_name,
      is_active: !!form.is_active,
      ...(roleField !== undefined ? { global_role: roleField } : {}),
    };

    try {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[EditUserDialog] PATCH /users/%s/ payload:", userId, payload);
      }

      await api.patch(`/users/${encodeURIComponent(userId)}/`, payload);

      // Le parent doit recharger la liste pour voir les nouvelles valeurs
      onSaved?.();
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[EditUserDialog] PATCH error:", status, data || e?.message || e);
      }

      let msg = "Échec de la mise à jour. Vérifie les champs et réessaie.";
      if (data && typeof data === "object") {
        const keys = Object.keys(data);
        if (keys.length) {
          const field = keys[0];
          const detail = Array.isArray(data[field]) ? data[field][0] : data[field];
          msg = `${field}: ${detail}`;
        } else if (data.detail) {
          msg = String(data.detail);
        }
      } else if (typeof data === "string") {
        msg = data;
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = normalizeRoleOptions(availableRoles);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      // Force un remount quand on change d’utilisateur pour éviter un état résiduel
      key={open ? userId : "closed"}
    >
      <DialogTitle>Modifier l’utilisateur</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <p>Chargement…</p>
        ) : (
          <>
            {error && <p style={{ color: "red" }}>{error}</p>}

            <TextField
              margin="dense"
              label="Username"
              name="username"
              value={form.username}
              fullWidth
              disabled
            />

            <TextField
              margin="dense"
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              type="email"
            />

            <TextField
              margin="dense"
              label="Prénom"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              margin="dense"
              label="Nom"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch checked={!!form.is_active} name="is_active" onChange={handleToggle} />
              }
              label="Actif"
              sx={{ mt: 1 }}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="role-label">Rôle</InputLabel>
              <Select
                labelId="role-label"
                label="Rôle"
                name="global_role"
                value={form.global_role || ""}
                onChange={handleChange}
                disabled={!roleOptions.length}
              >
                <MenuItem value="">
                  <em>Aucun</em>
                </MenuItem>
                {roleOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button onClick={handleSave} disabled={saving || loading} variant="contained">
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditUserDialog.propTypes = {
  /** Ouvre/ferme la modale */
  open: PropTypes.bool.isRequired,
  /** ID utilisateur ciblé (string ou number, selon ton back) */
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Callback fermeture (ferme la modale) */
  onClose: PropTypes.func.isRequired,
  /** Callback post-save (pour recharger la liste par ex.) */
  onSaved: PropTypes.func,
  /**
   * Liste des rôles disponibles :
   *  - soit ["Admin", "ED", "GA", ...]
   *  - soit [{ id, name } / { slug } / { value,label } ...]
   */
  availableRoles: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        slug: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ])
  ),
};

EditUserDialog.defaultProps = {
  onSaved: () => {},
  availableRoles: [],
};
